import React, { useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Menu, Icon } from 'semantic-ui-react';
import { FilePicker, Popup } from '../../lib/custom-ui';
import { useSelector } from 'react-redux';
import Config from '../../constants/Config';
import selectors from '../../selectors';

import styles from './AttachmentAddStep.module.scss';

const AttachmentAddStep = React.memo(({ onCreate, onClose, onGoogleDriveClick }) => {
  const [t] = useTranslation();
  const [hasGoogleIntegration, setHasGoogleIntegration] = useState(false);
  const accessToken = useSelector(selectors.selectAccessToken);

  useEffect(() => {
    // Check if Google Drive is connected
    const checkStatus = async () => {
      try {
        const response = await fetch(`${Config.SERVER_BASE_URL}/api/integrations/google/status`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (response.ok) {
          const data = await response.json();
          setHasGoogleIntegration(data.isConnected);
        }
      } catch (e) {
        console.error('Error checking Google status', e);
      }
    };
    checkStatus();
  }, [accessToken]);

  const handleFileSelect = useCallback(
    file => {
      onCreate({
        file,
      });
      onClose();
    },
    [onCreate, onClose]
  );

  const handleGoogleDriveClick = useCallback(() => {
    onClose(); // Close the popup first
    if (onGoogleDriveClick) {
      onGoogleDriveClick();
    }
  }, [onClose, onGoogleDriveClick]);

  return (
    <>
      <Popup.Header>
        {t('common.addAttachment', {
          context: 'title',
        })}
      </Popup.Header>
      <Popup.Content>
        <Menu secondary vertical className={styles.menu}>
          <FilePicker multiple onSelect={handleFileSelect}>
            <Menu.Item className={styles.menuItem}>
              {t('common.fromComputer', {
                context: 'title',
              })}
            </Menu.Item>
          </FilePicker>

          {hasGoogleIntegration && (
            <Menu.Item className={styles.menuItem} onClick={handleGoogleDriveClick}>
              <Icon name="google drive" /> From Google Drive
            </Menu.Item>
          )}
        </Menu>
        <hr className={styles.divider} />
        <div className={styles.tip}>
          {t('common.pressPasteShortcutToAddAttachmentFromClipboard')}
        </div>
      </Popup.Content>
    </>
  );
});

AttachmentAddStep.propTypes = {
  onCreate: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onGoogleDriveClick: PropTypes.func, // Optional because it might not be passed in all contexts
};

AttachmentAddStep.defaultProps = {
  onGoogleDriveClick: undefined,
};

export default AttachmentAddStep;
